import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Send } from "lucide-react";
import { ExportImportButtons } from "@/components/ExportImportButtons";

export default function Inventory() {
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { brand: "", product: "", model_number: "", part_name: "", part_description: "", item_category: "", qty_brt: 0, qty_ktm: 0, unit_cost: 0 };
  const [form, setForm] = useState(blank);
  const [search, setSearch] = useState("");

  const load = () => api.get("/parts").then((r) => setParts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/parts/${editing.id}`, form);
      else await api.post("/parts", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/parts/${id}`); toast.success("Deleted"); load(); };
  const openEdit = (p) => { setEditing(p); setForm({ ...blank, ...p }); setOpen(true); };

  const [dispOpen, setDispOpen] = useState(false);
  const [dispPart, setDispPart] = useState(null);
  const blankDisp = {
    qty: 1, from: "KTM", complaint_id: "",
    courier_company: "", docket_number: "", dispatch_date_input: "",
    expected_delivery_date: "", freight_charge: "",
    sent_from: "", sent_to: "", receiver_name: "", courier_remarks: "",
    spare_photo_url: "", transport_bill_url: "",
    pod_number: "",
  };
  const [dispForm, setDispForm] = useState(blankDisp);
  const [generatingPod, setGeneratingPod] = useState(false);
  const openDispatch = (p) => {
    setDispPart(p);
    setDispForm({ ...blankDisp, sent_from: p?.location || "KTM" });
    setDispOpen(true);
  };
  // Convert a file (image/pdf) to a base64 data URL.
  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
  const onSparePhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Spare photo must be an image"); return; }
    if (f.size > 4_500_000) { toast.error("Spare photo too large (max ~4 MB)"); return; }
    const url = await readFileAsDataUrl(f);
    setDispForm((s) => ({ ...s, spare_photo_url: url }));
  };
  const onTransportBillChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) { toast.error("Transport bill must be image or PDF"); return; }
    if (f.size > 6_000_000) { toast.error("Transport bill too large (max ~6 MB)"); return; }
    const url = await readFileAsDataUrl(f);
    setDispForm((s) => ({ ...s, transport_bill_url: url }));
  };
  const generatePOD = async () => {
    if (!dispForm.complaint_id.trim()) { toast.error("Complaint ID is required to generate POD"); return; }
    if (!dispForm.qty || dispForm.qty <= 0) { toast.error("Quantity must be > 0"); return; }
    if (!dispForm.spare_photo_url) { toast.error("Spare part photo is required"); return; }
    setGeneratingPod(true);
    try {
      const { data } = await api.post("/spares/pod", {
        complaint_id: dispForm.complaint_id,
        part_id: dispPart?.id,
        part_name: dispPart?.part_name,
        part_code: dispPart?.part_code || dispPart?.model_number,
        quantity: Number(dispForm.qty),
        spare_photo_url: dispForm.spare_photo_url,
        transport_bill_url: dispForm.transport_bill_url || undefined,
        courier_company: dispForm.courier_company,
        docket_number: dispForm.docket_number,
        dispatch_date_input: dispForm.dispatch_date_input,
        expected_delivery_date: dispForm.expected_delivery_date,
        freight_charge: dispForm.freight_charge !== "" ? Number(dispForm.freight_charge) : undefined,
        sent_from: dispForm.sent_from,
        sent_to: dispForm.sent_to,
        receiver_name: dispForm.receiver_name,
        courier_remarks: dispForm.courier_remarks,
      });
      setDispForm((f) => ({ ...f, pod_number: data.pod_number }));
      toast.success("POD generated", { description: data.pod_number });
    } catch (e) {
      toast.error("POD generation failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setGeneratingPod(false);
    }
  };
  const submitDispatch = async () => {
    if (!dispForm.spare_photo_url) {
      toast.error("Spare part photo is required before dispatch");
      return;
    }
    if (!dispForm.pod_number) {
      toast.error("Generate POD first", { description: "Dispatch cannot be marked sent without a POD" });
      return;
    }
    try {
      await api.post(`/parts/${dispPart.id}/dispatch`, {
        qty: dispForm.qty, from: dispForm.from,
        complaint_id: dispForm.complaint_id,
        courier: dispForm.courier_company,
        pod_number: dispForm.pod_number,
      });
      toast.success("Dispatched");
      setDispOpen(false);
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };
  const downloadPOD = () => {
    if (!dispForm.pod_number) return;
    downloadBlob({
      url: `/spares/pod/${encodeURIComponent(dispForm.pod_number)}/pdf`,
      filename: `${dispForm.pod_number}.pdf`,
    });
  };

  const filtered = parts.filter((p) =>
    !search || `${p.part_name} ${p.brand} ${p.product} ${p.model_number}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Parts Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">Stock by warehouse — BRT and KTM.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportImportButtons kind="parts_inventory" label="Part Inventory" onDone={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-part-btn"><Plus size={14} className="mr-1" /> Add Part</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Part</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Part Name"><Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} data-testid="part-name-input" /></Fld>
              <Fld label="Item Category"><Input value={form.item_category||""} onChange={(e) => setForm({ ...form, item_category: e.target.value })} /></Fld>
              <Fld label="Brand"><Input value={form.brand||""} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Fld>
              <Fld label="Product"><Input value={form.product||""} onChange={(e) => setForm({ ...form, product: e.target.value })} /></Fld>
              <Fld label="Model #"><Input value={form.model_number||""} onChange={(e) => setForm({ ...form, model_number: e.target.value })} /></Fld>
              <Fld label="Description"><Input value={form.part_description||""} onChange={(e) => setForm({ ...form, part_description: e.target.value })} /></Fld>
              <Fld label="Qty @ BRT"><Input type="number" value={form.qty_brt} onChange={(e) => setForm({ ...form, qty_brt: parseInt(e.target.value)||0 })} /></Fld>
              <Fld label="Qty @ KTM"><Input type="number" value={form.qty_ktm} onChange={(e) => setForm({ ...form, qty_ktm: parseInt(e.target.value)||0 })} /></Fld>
              <Fld label="Unit Cost (NPR)"><Input type="number" value={form.unit_cost||0} onChange={(e) => setForm({ ...form, unit_cost: parseFloat(e.target.value)||0 })} /></Fld>
              <Fld label="Part Code"><Input value={form.part_code||""} onChange={(e) => setForm({ ...form, part_code: e.target.value })} data-testid="part-code-input" /></Fld>
              <Fld label="Condition">
                <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                        value={form.condition || "Fresh"}
                        onChange={(e) => setForm({ ...form, condition: e.target.value })}
                        data-testid="part-condition-input">
                  {["Fresh", "Damaged", "Refurbished", "Used", "Scrap"].map((c) =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </Fld>
              <Fld label="Default Warehouse">
                <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                        value={form.warehouse_default || ""}
                        onChange={(e) => setForm({ ...form, warehouse_default: e.target.value })}
                        data-testid="part-warehouse-input">
                  <option value="">—</option><option value="KTM">KTM</option><option value="BRT">BRT</option>
                </select>
              </Fld>
              <Fld label="Rack Number"><Input value={form.rack_number||""} onChange={(e) => setForm({ ...form, rack_number: e.target.value })} data-testid="part-rack-input" /></Fld>
              <Fld label="Box Number"><Input value={form.box_number||""} onChange={(e) => setForm({ ...form, box_number: e.target.value })} data-testid="part-box-input" /></Fld>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-part-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-4">
        <Input placeholder="Search part, brand, model..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search" />
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full dense-table">
            <thead className="bg-slate-50 border-b"><tr>
              <Th>Part</Th><Th>Code</Th><Th>Brand</Th><Th>Product / Model</Th>
              <Th>Condition</Th><Th>Rack / Box</Th>
              <Th align="right">BRT</Th><Th align="right">KTM</Th><Th align="right">Total</Th><Th align="right">Unit Cost</Th><Th align="right">Actions</Th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => {
                const cond = p.condition || "Fresh";
                const condColor = {
                  "Fresh": "bg-emerald-50 text-emerald-700 border-emerald-200",
                  "Damaged": "bg-rose-50 text-rose-700 border-rose-200",
                  "Refurbished": "bg-indigo-50 text-indigo-700 border-indigo-200",
                  "Used": "bg-sky-50 text-sky-700 border-sky-200",
                  "Scrap": "bg-slate-100 text-slate-700 border-slate-300",
                }[cond] || "bg-slate-50 border-slate-200 text-slate-700";
                return (
                <tr key={p.id} data-testid={`part-row-${p.id}`}>
                  <td className="font-medium text-slate-900">{p.part_name}<div className="text-xs text-slate-500">{p.part_description}</div></td>
                  <td className="font-mono text-xs">{p.part_code || "—"}</td>
                  <td>{p.brand}</td>
                  <td>{p.product} <span className="text-slate-500 font-mono text-xs">{p.model_number}</span></td>
                  <td><span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${condColor}`} data-testid={`part-condition-${p.id}`}>{cond}</span></td>
                  <td className="text-xs font-mono">{p.rack_number || "—"} / {p.box_number || "—"}</td>
                  <td className="text-right font-mono">{p.qty_brt}</td>
                  <td className="text-right font-mono">{p.qty_ktm}</td>
                  <td className="text-right font-mono font-semibold">{p.qty_brt + p.qty_ktm}</td>
                  <td className="text-right font-mono">NPR {p.unit_cost?.toLocaleString() || 0}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDispatch(p)} data-testid={`dispatch-part-${p.id}`}><Send size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Edit size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(p.id)}><Trash2 size={14} /></Button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={dispOpen} onOpenChange={setDispOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Dispatch Part: {dispPart?.part_name}</DialogTitle><DialogDescription>Upload spare photo (required), optional transport bill, courier details and generate POD before marking dispatch as sent.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Fld label="From Warehouse">
                <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm" value={dispForm.from} onChange={(e) => setDispForm({ ...dispForm, from: e.target.value })}>
                  <option value="KTM">KTM (Kathmandu)</option>
                  <option value="BRT">BRT</option>
                </select>
              </Fld>
              <Fld label="Quantity"><Input type="number" min={1} value={dispForm.qty} onChange={(e) => setDispForm({ ...dispForm, qty: parseInt(e.target.value)||1 })} data-testid="dispatch-qty-input" /></Fld>
              <Fld label="Complaint ID (UUID)">
                <Input value={dispForm.complaint_id} onChange={(e) => setDispForm({ ...dispForm, complaint_id: e.target.value })} placeholder="paste complaint UUID" data-testid="dispatch-complaint-id" />
              </Fld>
            </div>

            <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-2">
                Spare Part Photo · Required
              </div>
              <Input type="file" accept="image/*" onChange={onSparePhotoChange} data-testid="spare-photo-input" />
              {dispForm.spare_photo_url && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={dispForm.spare_photo_url} alt="spare" className="h-16 w-16 object-cover rounded border border-slate-200" />
                  <Button size="sm" variant="outline" onClick={() => setDispForm({ ...dispForm, spare_photo_url: "" })}>Remove</Button>
                </div>
              )}
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                Transport / Courier Bill · Optional (image or PDF, can add later)
              </div>
              <Input type="file" accept="image/*,application/pdf" onChange={onTransportBillChange} data-testid="transport-bill-input" />
              {dispForm.transport_bill_url && (
                <div className="mt-2 text-xs text-emerald-700 flex items-center justify-between">
                  <span>Bill attached ({dispForm.transport_bill_url.startsWith("data:application/pdf") ? "PDF" : "Image"})</span>
                  <Button size="sm" variant="outline" onClick={() => setDispForm({ ...dispForm, transport_bill_url: "" })}>Remove</Button>
                </div>
              )}
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">Transport / Courier Details</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Fld label="Courier Company"><Input value={dispForm.courier_company} onChange={(e) => setDispForm({ ...dispForm, courier_company: e.target.value })} data-testid="courier-company-input" placeholder="e.g. Nepal Logistics" /></Fld>
                <Fld label="Docket / Consignment #"><Input value={dispForm.docket_number} onChange={(e) => setDispForm({ ...dispForm, docket_number: e.target.value })} data-testid="docket-number-input" /></Fld>
                <Fld label="Dispatch Date"><Input type="date" value={dispForm.dispatch_date_input} onChange={(e) => setDispForm({ ...dispForm, dispatch_date_input: e.target.value })} data-testid="dispatch-date-input" /></Fld>
                <Fld label="Expected Delivery Date"><Input type="date" value={dispForm.expected_delivery_date} onChange={(e) => setDispForm({ ...dispForm, expected_delivery_date: e.target.value })} data-testid="expected-delivery-input" /></Fld>
                <Fld label="Freight Charge (NPR)"><Input type="number" min={0} value={dispForm.freight_charge} onChange={(e) => setDispForm({ ...dispForm, freight_charge: e.target.value })} data-testid="freight-charge-input" /></Fld>
                <Fld label="Sent From"><Input value={dispForm.sent_from} onChange={(e) => setDispForm({ ...dispForm, sent_from: e.target.value })} data-testid="sent-from-input" placeholder="e.g. KTM Warehouse" /></Fld>
                <Fld label="Sent To (Tech / Store)"><Input value={dispForm.sent_to} onChange={(e) => setDispForm({ ...dispForm, sent_to: e.target.value })} data-testid="sent-to-input" placeholder="e.g. Hari · Pokhara" /></Fld>
                <Fld label="Receiver Name"><Input value={dispForm.receiver_name} onChange={(e) => setDispForm({ ...dispForm, receiver_name: e.target.value })} data-testid="receiver-name-input" placeholder="optional" /></Fld>
                <Fld label="Remarks"><Input value={dispForm.courier_remarks} onChange={(e) => setDispForm({ ...dispForm, courier_remarks: e.target.value })} data-testid="courier-remarks-input" /></Fld>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">POD (Proof of Dispatch)</div>
              {!dispForm.pod_number && (
                <>
                  <div className="text-xs text-amber-700 mb-2">POD must be generated before dispatch. Spare photo is required.</div>
                  <Button size="sm" onClick={generatePOD} disabled={generatingPod || !dispForm.complaint_id || !dispForm.spare_photo_url} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="generate-pod-btn">
                    {generatingPod ? "Generating..." : "Generate POD"}
                  </Button>
                </>
              )}
              {dispForm.pod_number && (
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm text-emerald-800">
                    POD <span className="font-mono font-semibold">{dispForm.pod_number}</span> generated.
                  </div>
                  <Button size="sm" variant="outline" onClick={downloadPOD} data-testid="download-pod-btn">Download PDF</Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispOpen(false)}>Cancel</Button>
            <Button onClick={submitDispatch} disabled={!dispForm.pod_number || !dispForm.spare_photo_url} className="bg-slate-900 text-white disabled:opacity-50" data-testid="confirm-dispatch-btn">Dispatch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Fld({ label, children }) { return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>{children}</div>; }
function Th({ children, align }) { return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold`}>{children}</th>; }
